import { can, canSeeLoadRates, retrieveCustomization } from '../../../supplementary_components/userDataDelegate';
import { resolveDriverPayoutTrucks, shouldShowDriverPayout } from '../../../supplementary_components/driverPayoutUtils';

export const LOAD_DISPATCH_INFO_KEY = 'loadDispatchInfo';

export const DISPATCH_SECTIONS = ['header', 'body', 'footer'];

/**
 * stopOnly: only valid in the body (repeated per stop); all other fields describe the whole
 * load and are only valid in the header or footer.
 * locked: always present in the body; can be moved but not removed.
 * requiresDriverPayout: value is hidden from users who cannot see driver payouts.
 */
export const DISPATCH_FIELDS = [
    { key: 'loadNumber', label: 'Load Number' },
    { key: 'brokerageName', label: 'Brokerage Name' },
    { key: 'refNumber', label: 'Ref Number' },
    { key: 'puDel', label: 'PU/DEL', stopOnly: true },
    { key: 'stopType', label: 'Stop Type', note: 'Live, Hook or Drop', stopOnly: true },
    { key: 'stopTime', label: 'Stop Time', stopOnly: true, locked: true },
    { key: 'stopAddress', label: 'Stop Address', stopOnly: true, locked: true },
    { key: 'payoutSummary', label: 'Driver Payout / Loaded Miles / RPM', note: 'Lease/owner, or company truck with a percentage driver', requiresDriverPayout: true },
    { key: 'mileageBlock', label: 'Mileage Block' },
    { key: 'deadheadMiles', label: 'Deadhead Miles' },
    { key: 'loadedMiles', label: 'Loaded Miles' },
    { key: 'driverPayout', label: 'Driver Payout', note: 'Lease/owner, or company truck with a percentage driver', requiresDriverPayout: true },
    { key: 'totalRpm', label: 'Total RPM', note: 'Lease/owner, or company truck with a percentage driver', requiresDriverPayout: true },
    { key: 'loadedRpm', label: 'Loaded RPM', note: 'Lease/owner, or company truck with a percentage driver', requiresDriverPayout: true },
];

export const DISPATCH_FIELDS_BY_KEY = Object.fromEntries(DISPATCH_FIELDS.map((f) => [f.key, f]));

/** Pairs of fields that cannot be used together in one format. */
const EXCLUSIVE_FIELD_PAIRS = [
    ...['mileageBlock', 'deadheadMiles', 'loadedMiles', 'driverPayout', 'totalRpm', 'loadedRpm']
        .map((key) => ['payoutSummary', key]),
    ['mileageBlock', 'deadheadMiles'],
    ['mileageBlock', 'loadedMiles'],
    ['totalRpm', 'loadedRpm'],
];

/** True when a field cannot be added because a field it excludes is already used. */
export const isDispatchFieldExcluded = (key, usedKeys) => EXCLUSIVE_FIELD_PAIRS.some(
    ([a, b]) => (a === key && usedKeys.has(b)) || (b === key && usedKeys.has(a))
);

export const canPlaceDispatchField = (key, section) => {
    const field = DISPATCH_FIELDS_BY_KEY[key];
    if (!field) return false;
    return field.stopOnly ? section === 'body' : section !== 'body';
};

export const DEFAULT_LOAD_DISPATCH_INFO = {
    header: 'Load#: {loadNumber}\n\nREF/APPT#:\n{refNumber}',
    body: '\n————————————\n\n{puDel}: {stopTime}\n|\n{stopAddress}',
    footer: '\n————————————\n\n{payoutSummary}',
    /** Plain text added below the footer for high value loads. */
    highValueWarning: '',
};

export const DISPATCH_TOKEN_PATTERN = new RegExp(`\\{(${DISPATCH_FIELDS.map((f) => f.key).join('|')})\\}`, 'g');

/** Split a template section into text and field parts. */
export const parseDispatchTemplate = (text) => {
    const parts = [];
    let last = 0;
    for (const match of (text || '').matchAll(DISPATCH_TOKEN_PATTERN)) {
        if (match.index > last) parts.push({ type: 'text', value: text.slice(last, match.index) });
        parts.push({ type: 'field', key: match[1] });
        last = match.index + match[0].length;
    }
    if (last < (text || '').length) parts.push({ type: 'text', value: text.slice(last) });
    return parts;
};

export const normalizeLoadDispatchInfo = (raw) => {
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_LOAD_DISPATCH_INFO };
    const result = {};
    for (const section of DISPATCH_SECTIONS) {
        result[section] = typeof raw[section] === 'string' ? raw[section] : '';
    }
    const highValueWarning = typeof raw.highValueWarning === 'string' ? raw.highValueWarning : '';
    result.highValueWarning = highValueWarning;
    const countInBody = (key) => parseDispatchTemplate(result.body).filter((p) => p.key === key).length;
    const bodyIsValid = DISPATCH_FIELDS.filter((f) => f.locked).every((f) => countInBody(f.key) === 1);
    const placementIsValid = DISPATCH_SECTIONS.every((section) => parseDispatchTemplate(result[section])
        .every((p) => p.type === 'text' || canPlaceDispatchField(p.key, section)));
    const usedKeys = new Set(DISPATCH_SECTIONS.flatMap((section) =>
        parseDispatchTemplate(result[section]).filter((p) => p.type === 'field').map((p) => p.key)));
    const exclusionsAreValid = [...usedKeys].every((key) => !isDispatchFieldExcluded(key, usedKeys));
    return bodyIsValid && placementIsValid && exclusionsAreValid
        ? result
        : { ...DEFAULT_LOAD_DISPATCH_INFO, highValueWarning };
};

export const retrieveLoadDispatchInfoFormat = () =>
    normalizeLoadDispatchInfo(retrieveCustomization(LOAD_DISPATCH_INFO_KEY));

export const isDefaultLoadDispatchInfo = (format) =>
    [...DISPATCH_SECTIONS, 'highValueWarning'].every((key) => (format[key] ?? '') === DEFAULT_LOAD_DISPATCH_INFO[key]);

const isBlank = (line) => line.trim() === '';

/**
 * Render one section. A line whose fields are all empty is dropped, together with a
 * label line ending in ':' right above it, and a blank line that would be left dangling.
 * When only some fields on a line are empty, the separators next to them are dropped.
 */
const renderSection = (template, values) => {
    const lines = (template || '').split('\n').map((line) => {
        const parts = parseDispatchTemplate(line);
        const fields = parts.filter((p) => p.type === 'field');
        if (fields.length === 0) return { text: line, hasFields: false };

        const filled = fields.map((p) => values[p.key] ?? '');
        if (filled.every((v) => v === '')) return { dropped: true };

        let text = '';
        let fieldIndex = -1;
        let pendingSeparator = '';
        let anyFilled = false;
        parts.forEach((part, i) => {
            if (part.type === 'text') {
                const isLeading = i === 0;
                const isTrailing = i === parts.length - 1;
                if (isLeading) text += part.value;
                else if (isTrailing) text += part.value;
                else pendingSeparator = part.value;
                return;
            }
            fieldIndex += 1;
            const value = filled[fieldIndex];
            if (value === '') return;
            if (anyFilled) text += pendingSeparator;
            pendingSeparator = '';
            text += value;
            anyFilled = true;
        });
        return { text, hasFields: true };
    });

    const kept = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.dropped) {
            kept.push(line);
            continue;
        }
        const previous = kept[kept.length - 1];
        if (previous && !previous.hasFields && previous.text.trim().endsWith(':')) {
            kept.pop();
        }
        let nextIndex = i + 1;
        while (nextIndex < lines.length && lines[nextIndex].dropped) nextIndex++;
        const next = lines[nextIndex];
        const blankBefore = kept[kept.length - 1];
        if (blankBefore && !blankBefore.hasFields && isBlank(blankBefore.text) && (!next || isBlank(next.text ?? ''))) {
            kept.pop();
        }
        i = nextIndex - 1;
    }
    return kept.map((line) => line.text).join('\n');
};

const trimBlankLines = (text) => (text || '').replace(/^\s*\n/, '').trimEnd();

const hasOnlyEmptyFields = (template, values) => {
    const fields = parseDispatchTemplate(template).filter((p) => p.type === 'field');
    return fields.length > 0 && fields.every((p) => (values[p.key] ?? '') === '');
};

/** Leftover separator lines (dashes, rules) with no words or numbers. */
const isDividerOnly = (text) => text.split('\n').every((line) => !/[A-Za-z0-9]/.test(line));

/**
 * Build the copied text from a template and dispatch data (see buildDispatchDataFromLoad).
 * A header or footer whose fields are all empty is skipped when nothing printable would remain,
 * so a lone divider is not left behind. Text on other lines is kept, and a lone footer divider
 * is kept when a high value warning follows it.
 * For high value loads the warning text follows the footer after one blank line.
 * A broker's custom header (set per company on the broker) goes above everything, followed by one blank line.
 */
export const renderLoadDispatchInfo = (format, data) => {
    const common = Object.fromEntries(DISPATCH_FIELDS.filter((f) => !f.stopOnly).map((f) => [f.key, data[f.key] ?? '']));
    common.refNumber = data.stops.map((s) => s.refNumber).filter(Boolean).join('\n');
    const warning = data.isHighValue ? trimBlankLines(format.highValueWarning) : '';
    const renderLoadSection = (template, keepDivider = false) => {
        const rendered = renderSection(template, common);
        if (!hasOnlyEmptyFields(template, common)) return rendered;
        const meaningful = trimBlankLines(rendered);
        if (!meaningful || (isDividerOnly(meaningful) && !keepDivider)) return '';
        return rendered;
    };
    const parts = [renderLoadSection(format.header)];
    for (const stop of data.stops) {
        parts.push(renderSection(format.body, {
            puDel: stop.puDel ?? '',
            stopType: stop.stopType ?? '',
            stopTime: stop.stopTime ?? '',
            stopAddress: stop.stopAddress ?? '',
        }));
    }
    parts.push(renderLoadSection(format.footer, Boolean(warning)));
    const text = parts.join('\n').replace(/^\s*\n/, '').trimEnd();
    const customHeader = trimBlankLines(data.customHeader);
    return [customHeader, text, warning].filter(Boolean).join('\n\n');
};

const formatMoney = (amount) => `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
})}`;

const formatMiles = (miles) => `${miles.toLocaleString('en-US')} mi`;

const buildMileageBlock = (miles, deadhead) => {
    if (deadhead > 0) {
        return [
            `Deadhead: ${formatMiles(deadhead)}`,
            `Load Miles: ${formatMiles(miles)}`,
            `Total Miles: ${formatMiles(miles + deadhead)}`,
        ].join('\n');
    }
    return miles > 0 ? `Miles: ${formatMiles(miles)}` : '';
};

/** Loaded RPM is driver payout per loaded mile; total RPM also counts deadhead. */
const buildPayoutAndMileageFields = (payout, miles, deadhead) => {
    const hasPayout = Number.isFinite(payout) && payout > 0;
    const perMile = (distance) => (hasPayout && distance > 0 ? `$${(payout / distance).toFixed(2)}` : '');
    const driverPayout = hasPayout ? formatMoney(payout) : '';
    let payoutSummary = '';
    if (hasPayout) {
        payoutSummary = miles > 0 ? `${driverPayout} / ${formatMiles(miles)} / ${perMile(miles)}` : driverPayout;
    }
    return {
        payoutSummary,
        driverPayout,
        mileageBlock: buildMileageBlock(miles, deadhead),
        deadheadMiles: deadhead > 0 ? formatMiles(deadhead) : '',
        loadedMiles: miles > 0 ? formatMiles(miles) : '',
        totalRpm: perMile(miles + deadhead),
        loadedRpm: perMile(miles),
    };
};

const formatStopType = (puDel, loadingType) => {
    if (loadingType === 'live') return 'LIVE';
    if (loadingType !== 'hook&drop') return '';
    if (puDel === 'PU') return 'HOOK';
    if (puDel === 'DEL') return 'DROP';
    return 'HOOK&DROP';
};

const canSeeDriverPayoutAmounts = () => can('DRIVER_PAYOUTS', 'VIEW') && canSeeLoadRates();

const formatStopAddress = (address) => {
    if (!address) return '';
    const cityLine = `${address.city || ''}, ${address.state || ''} ${address.postalCode || ''}`.trim();
    return [address.companyName, address.addressLine1, address.addressLine2, cityLine]
        .filter((line) => line && line !== ',')
        .join('\n');
};

const resolveCurrentDriverPayout = (load) => {
    if (!shouldShowDriverPayout(load)) return null;
    const trucks = resolveDriverPayoutTrucks(load);
    const currentId = load.currentTruck?.id != null ? String(load.currentTruck.id) : null;
    const isSet = (t) => t && t.payout !== null && t.payout !== undefined && t.payout !== '';
    const current = trucks.find((t) => t.truckId === currentId);
    const entry = isSet(current) ? current : trucks.find(isSet);
    return entry ? Number(entry.payout) : null;
};

export const buildDispatchDataFromLoad = (load) => {
    const payout = canSeeDriverPayoutAmounts() ? resolveCurrentDriverPayout(load) : null;
    return {
        loadNumber: load.loadNumber,
        brokerageName: load.broker?.brokerageName,
        customHeader: load.broker?.customHeader,
        isHighValue: load.loadType === 'FTLHighValue',
        stops: (load.stops || []).map((stop) => ({
            refNumber: stop.refNumber,
            puDel: stop.stopType,
            stopType: formatStopType(stop.stopType, stop.loadingType),
            stopTime: `${stop.apptDate || ''} ${stop.apptTime || ''}`.trim(),
            stopAddress: formatStopAddress(stop.address),
        })),
        ...buildPayoutAndMileageFields(payout, Number(load.mileage) || 0, Number(load.deadhead) || 0),
    };
};

export const buildSampleDispatchData = () => ({
    loadNumber: '12345678',
    brokerageName: 'LANDSTAR RANGER INC',
    isHighValue: true,
    stops: [
        {
            refNumber: 'RR-113344',
            puDel: 'PU',
            stopType: 'LIVE',
            stopTime: '01/01/26 12:00 PM',
            stopAddress: 'Shipping Company\n123 Main Street\nLos Angeles, CA 90012',
        },
        {
            puDel: 'DEL',
            stopType: 'DROP',
            stopTime: '01/02/26 12:00 PM',
            stopAddress: 'Receiving Company\n710 Beach Street\nSan Francisco, CA 94109',
        },
    ],
    ...buildPayoutAndMileageFields(canSeeDriverPayoutAmounts() ? 1200 : null, 382, 45),
});
